import Enumerable from "linq";
import { EditPhaseKinds } from "../../../../domain/document";
import { IHisInformationApplicable, instanceOfIHisInformationSelectInputable, instanceOfIHisInformationApplicable, instanceOfISubNameSpecifiable, DateItem } from "../../../../domain/document/items";
import { IDocumentEditable } from "..";
import { HisItemBehaviorFactory, HisItemParameter, InformationKinds, ScreenKinds } from "../../../../domain/his/behavior";
import { HisInformation, HisInformationListItem, HisInformationItem, HisInformationForceUpdateParameter } from "../../../../domain/his";
import { IHisInformationGroupSource } from "../../../../domain/his/groupInput/HisInformationGroupSource";
import { DesignatedDoctorNumberApplicator } from "./DesignatedDoctorNumberApplicator";
import { User } from "../../../../domain/user";
import { Department } from "../../../../domain/department";
import {
  HisItemProgressAction,
  IHisItemAction,
  HisItemSelectAction,
  HisItemUpdateAction,
  instanceOfIHisItemSelectable,
  HisItemFormatUpdateAction,
} from "../../../../domain/his/action";
import { Table } from "../../../../domain/system/table";
import { LoggerFactory } from "../../../../utilities/logger";
import { AttendingDoctorApplicator, MischievousHisInformationApplicator, OrderDoctorApplicator } from ".";
import store from "../../../../store";
import { IBehaviorDocumentEditorMischievousArrangedObjectConfiguration, IConfiguration } from "../../../../domain/configuration";
import { DateInputMethodKinds } from "../../../../domain/document/inputMethodKinds";


export class HisInformationApplicator {
  constructor(private _document: IDocumentEditable) {}

  private readonly _logger = LoggerFactory.getLogger();
  
  /**
   * HIS項目の進捗入力（文書記載開始時、印刷時などに値を設定）
   * ※編集開始時に複数のトリガを与える場合はこちらを使用してください
   */
  applyByProgresses(info: HisInformation, progresses: EditPhaseKinds[], user?: User, department?: Department, orderUser?: User, attendingUser?: User): void {
    this.applyByAll(info);
    const isFirstEdit = progresses.indexOf(EditPhaseKinds.EditStartFirst) >= 0;
    for (const progress of progresses) {
      this._logger.debug("HIS情報初期化(" + progress.toString() + ", " + isFirstEdit + ")");
      this.applyByProgress(info, progress, user, department, orderUser, attendingUser, isFirstEdit);
    }
    if (this.shouldUpdateHisInformationOnEditStart && !isFirstEdit) {
      this._logger.debug("HIS情報毎回更新による値の上書きを行います。");
      this.applyByUpdate(info, true, this.shouldUpdateHisInformationEmptyObjectOnly);
      this._document.source.isNewHisInformationApplied = true;
    }
  }

  get shouldUpdateHisInformationOnEditStart(): boolean {
    return this._document.source.attributes.behavior.shouldUpdateHisInformationOnEditStart;
  }

  get shouldUpdateHisInformationEmptyObjectOnly(): boolean {
    return this._document.source.attributes.behavior.shouldUpdateHisInformationEmptyObjectOnly;
  }

  get hostName(): string {
    return store.getters.hostName;
  }

  /** HIS項目の進捗入力（文書記載開始時、印刷時などに値を設定） */
  applyByProgress(info: HisInformation, progress: EditPhaseKinds, user?: User, department?: Department, orderUser?: User, attendingUser?: User, isFirstEdit?: boolean): void {
    const action = new HisItemProgressAction(this._document, info, progress, user, department, orderUser, attendingUser, isFirstEdit, this.hostName, null);
    this.applyByAction(action, progress);
  }

  /** HIS項目の選択入力（ユーザや診療科などの選択画面から値を設定） */
  applyBySelect(item: IHisInformationApplicable, source: IHisInformationGroupSource, itemSetId: number): void {
    const action = new HisItemSelectAction(this._document, item, source, itemSetId);
    
    if (this.applyByLinkedUserHisItemSelect(item, action)) {
      // 文書内のオブジェクトを連動して更新済み
      return;
    }
    this.applyByAction(action);
  }

  private applyByLinkedUserHisItemSelect(item: IHisInformationApplicable, action: HisItemSelectAction): boolean {
    const orderDoctorApplicator = new OrderDoctorApplicator(this._document);
    if (orderDoctorApplicator.isTargetObject(item)) {
      // 指示医は文書内で連動して更新させる
      orderDoctorApplicator.apply(action);
      return true;
    }
    const attendingUserApplicator = new AttendingDoctorApplicator(this._document);
    if (attendingUserApplicator.isTargetObject(item)) {
      // 指導医は文書内で連動して更新させる
      attendingUserApplicator.apply(action);
      return true;
    }
    return false;
  }

  /** HIS項目の更新入力（患者情報更新ボタン押下時に値を設定） */
  applyByUpdate(info: HisInformation, isAutoUpdate: boolean, shouldUpdateEmptyObjectOnly: boolean): void {
    const action = new HisItemUpdateAction(this._document, info, isAutoUpdate, shouldUpdateEmptyObjectOnly);
    this.applyByAction(action);
  }

  /** 編集中のフォーマット最新化を契機とするHIS項目の更新処理 */
  public applyByFormatUpdate(info: HisInformation, user: User): void {
    // フォーマット最新化で全てのオブジェクトが入れ替えられているので、右クリックしたときに「データ選択」を出すフラグを立て直している
    this.applyByAll(info);

    const action = new HisItemFormatUpdateAction(this._document, info, user, this.hostName);
    this.applyByAction(action);
  }

  private applyByAction(action: IHisItemAction, progress?: EditPhaseKinds): void {
    let isManagerUpdated = false;
    for (let itemSetId = 0; itemSetId < action.applicableItems.length; itemSetId++) {
      for (const originalItem of action.applicableItems[itemSetId]) {
        // updateItemで更新された分が反映されていないのでitemSetIdとprivateIdで取得しなおしている
        const item = this._document.itemSets[itemSetId].items[originalItem.privateId] as IHisInformationApplicable;
        const cloned = item.clone() as IHisInformationApplicable;
        const behavior = HisItemBehaviorFactory.create(item.hisItemParameter.key, action.dateFormat, item.hisItemParameter.objectKind);
        const hisInfoValue = behavior.getValue(action.informationItems);
        const applicator = new MischievousHisInformationApplicator(this._document, null, false);
        if (applicator.shouldRollbackValue(item.hisItemParameter, progress)) {
          cloned.rollbackValue();
        } else {
          if (hisInfoValue.data.length === 0 && action instanceof HisItemUpdateAction) {
            // 「患者情報の更新」ボタンをクリックした処理について、HIS情報に含まれていない項目・分割生成できなかった項目は更新させない
            this._logger.debug("値が生成されていないため、HIS項目への値の適用をスキップします。key=" + hisInfoValue.key);
          } else {
            if (hisInfoValue.key === "WORKFLOW_MANAGE_OPERATOR") {
              // ワークフロー対応責任者が更新された場合は指定医番号も連動させる必要がある
              isManagerUpdated = true;
            }
            cloned.setHisInfoItems(hisInfoValue, applicator.shouldSkipInputMethodKindUpdate(item));
          }
        }
        if (instanceOfIHisItemSelectable(action)) {
          const selectedItems = Enumerable.from(action.selectedHisItemSets)
            .where(i => i.screenKind === item.hisItemParameter.screenKind)
            .select(s => s.items)
            .firstOrDefault();
          // selectedHisItemSetsはリアクティブになっているので、解除するためにconcatしている
          switch (item.hisItemParameter.screenKind) {
            case ScreenKinds.Department:
              const departments = selectedItems as Department[];
              cloned.setSelectedItems((!departments || departments.length === 0) ? null : departments.concat());
              break;
            case ScreenKinds.Doctor:
              const users = selectedItems as User[];
              cloned.setSelectedItems((!users || users.length === 0) ? null : users.concat());
              break;
            case ScreenKinds.List:
              const lists = selectedItems as HisInformationListItem[];
              cloned.setSelectedItems((!lists || lists.length === 0) ? null : lists.concat());
              break;
          }
        }
        cloned.adjustObjectHeight();
        cloned.adjustFontSize();
        this._document.updateItem(cloned, {
          itemSetId,
        });
      }
    }
    if (isManagerUpdated && action.managerIds) {
      // ワークフロー対応責任者から指定医番号への連動はグループ名が設定されていなくても連動する
      const designatedDoctorNumberApplicator = new DesignatedDoctorNumberApplicator(this._document, store.getters.configuration);
      designatedDoctorNumberApplicator.apply(this._document.source.designatedDoctorInformations, action.managerIds);
    }

    // 複数のHIS情報を、連結ではなく順番もしくはサブ名称に従って値を反映
    this.applyListItemsByAction(action);
  }

  private applyListItemsByAction(action: IHisItemAction): void {
    const configuration = (store.getters.configuration as IConfiguration).behavior.document.editor.mischievousObjects;
    this.applyListItemsWithConfiguration(action.applicableItems, action.hospitalizationItems, configuration.hospitalization);
    this.applyListItemsWithConfiguration(action.applicableItems, action.diseaseItems, configuration.disease);
  }

  private applyListItemsWithConfiguration(applicableItems: IHisInformationApplicable[][], hisInformationItems: HisInformationItem[], configuration: IBehaviorDocumentEditorMischievousArrangedObjectConfiguration): void {
    try {
      this.applyListItems(applicableItems, hisInformationItems, configuration.shouldApplyBySubName, this.createSubNameRegEx(configuration));
    } catch (e) {
      // エラーは発生させないようにしているが、予期しないエラーが発生したときも記載できるように、念のためcatchしている
      console.warn(e);
    }
  }

  createSubNameRegEx(configuration: IBehaviorDocumentEditorMischievousArrangedObjectConfiguration): RegExp {
    if (!configuration.shouldValidateSubNameFormat || !configuration.targetSubNameFormat) { return null; }
    return new RegExp(configuration.targetSubNameFormat);
  }

  private applyListItems(applicableItems: IHisInformationApplicable[][], hisInformationItems: HisInformationItem[], isBySubName: boolean, subNameRegEx: RegExp): void {
    if (!hisInformationItems || hisInformationItems.length === 0) { return; }

    for (let itemSetId = 0; itemSetId < applicableItems.length; itemSetId++) {
      for (const originalItem of applicableItems[itemSetId]) {
        // updateItemで更新された分が反映されていないのでitemSetIdとprivateIdで取得しなおしている
        const item = this._document.itemSets[itemSetId].items[originalItem.privateId] as IHisInformationApplicable;
        
        const itemIndex = isBySubName
          ? this.getItemIndexFromSubName(item, subNameRegEx)
          : this.getItemIndexFromItemSet(itemSetId, item);
        const mathcedHisInformations = this.extractIndexMatchedHisInformations(hisInformationItems, itemIndex);
        if (mathcedHisInformations.length === 0) {
          // 一致するものが無い場合は更新しない（空にはしない）
          continue;
        }
        const cloned = item.clone() as IHisInformationApplicable;
        const behavior = HisItemBehaviorFactory.create(item.hisItemParameter.key, "Y 年 M 月 D 日", item.hisItemParameter.objectKind);
        const hisInfoValue = behavior.getValue(mathcedHisInformations);
        if (hisInfoValue.data.length === 0) {
          // 入院日がなくて退院日がある場合、入院日をカレンダー入力に変更
          if (item.hisItemParameter.key === "ENTER_HOSP_DATE") {
            const leaveDate = Enumerable.from(mathcedHisInformations).where(h => h.name === "LEAVE_HOSP_DATE_TIME").firstOrDefault();
            this.updateInputMethodKind(cloned, leaveDate, itemSetId);
          }
          // 退院日がなくて入院日がある場合、退院日をカレンダー入力に変更
          if (item.hisItemParameter.key === "LEAVE_HOSP_DATE") {
            const enterDate = Enumerable.from(mathcedHisInformations).where(h => h.name === "ENTER_HOSP_DATE_TIME").firstOrDefault();
            this.updateInputMethodKind(cloned, enterDate, itemSetId);
          }

          // 一致するものが無い場合は更新しない（空にはしない）
          continue;
        }
        cloned.setHisInfoItems(hisInfoValue, false);
        cloned.adjustObjectHeight();
        cloned.adjustFontSize();
        this._document.updateItem(cloned, {
          itemSetId,
        });
      }
    }
  }

  private updateInputMethodKind(item: IHisInformationApplicable, hisItem: HisInformationItem, itemSetId: number): void {
    const dateItem = item as DateItem;
    if (!dateItem || !hisItem || !hisItem.value) { return; }
    dateItem.inputMethodKind = DateInputMethodKinds.Calendar;
    this._document.updateItem(dateItem, {
      itemSetId,
    });
  }

  private extractIndexMatchedHisInformations(sourceItems: HisInformationItem[], itemIndex: number): HisInformationItem[] {
    if (itemIndex == null || itemIndex < 0) { return []; }
    const items: HisInformationItem[] = [];
    const keys = Enumerable.from(sourceItems)
      .select(i => i.name)
      .distinct()
      .toArray();
    for (const key of keys) {
      const extractedHisInformations = sourceItems.filter(i => i.name === key);
      const targetHisInformation = extractedHisInformations[itemIndex];
      if (targetHisInformation) {
        items.push(targetHisInformation);
      }
    }
    return items;
  }

  private getItemIndexFromSubName(item: IHisInformationApplicable, subNameRegEx: RegExp): number {
    if (!instanceOfISubNameSpecifiable(item)) { return null; }
    if (!this.isSubNameFormatValid(item.subName, subNameRegEx)) {
      return null;
    }
    const extracted = this.extractLastDigit(item.subName);
    return (extracted == null)
      ? null
      : extracted - 1;
  }

  /**
   * サブ名称に数字が含まれている場合は無条件で値を適用していたが、
   * 想定外のオブジェクトのサブ名称に数字を含むフォーマットが多数配布されてしまったため、
   * 関係各所で相談した結果、命名規則による制限を設けることとなった
   */
  private isSubNameFormatValid(subName: string, regex: RegExp): boolean {
    if (!regex) { return true; }
    return regex.test(subName ? subName : "");
  }

  private extractLastDigit(value: string): number {
    if (!value) { return null; }
    const replaced = value
      .replace(/０/g, "0")
      .replace(/１/g, "1")
      .replace(/２/g, "2")
      .replace(/３/g, "3")
      .replace(/４/g, "4")
      .replace(/５/g, "5")
      .replace(/６/g, "6")
      .replace(/７/g, "7")
      .replace(/８/g, "8")
      .replace(/９/g, "9");
    
    const matched = replaced.match(/[0-9]+/g);
    if (!matched || matched.length === 0) { return null; }
    const lastValue = matched[matched.length - 1];
    return parseInt(lastValue, 10);
  }

  private getItemIndexFromItemSet(itemSetId: number, item: IHisInformationApplicable): number {
    // サブ名称が指定されているオブジェクトには格納しない
    if (instanceOfISubNameSpecifiable(item) && item.subName) { return null; }
    
    // オブジェクト数が多いときに遅くなりそうではあるが、基本的にはサブ名称で判定する設定になるはずなので、影響は無い見込み
    const items = Enumerable.from(this._document.itemSets[itemSetId].items)
      .where(i => !!i)
      .where(i => i.typeKey === item.typeKey)
      .where(i => instanceOfIHisInformationApplicable(i) && i.isHisInfo && i.hisItemParameter.key === item.hisItemParameter.key)
      .where(i => !instanceOfISubNameSpecifiable(i) || !i.subName)  // サブ名称が無いものだけ抽出する
      .toArray();
    
    // findIndex使いたいが、IE非対応
    for (let index = 0; index < items.length; ++index) {
      if (items[index].privateId === item.privateId) {
        return index;
      }
    }
    return null;
  }

  private applyByAll(info: HisInformation): void {
    if (!info) { return; }
    const items = this._document.itemSets.map(itemSet => itemSet.items
      .filter(instanceOfIHisInformationApplicable)
      .filter(item => item.hisItemParameter != null)
    );
    for (let itemSetId = 0; itemSetId < items.length; itemSetId++) {
      for (const originalItem of items[itemSetId]) {
        // updateItemで更新された分が反映されていないのでitemSetIdとprivateIdで取得しなおしている
        const item = this._document.itemSets[itemSetId].items[originalItem.privateId] as IHisInformationApplicable;
        const cloned = item.clone();
        if (instanceOfIHisInformationSelectInputable(cloned)) {
          cloned.isHisInfoSelectInputable = this.existHisInfoSelectValue(info, item.hisItemParameter);
        }
        this._document.updateItem(cloned, {
          itemSetId,
        });
      }
    }
  }
  
  private existHisInfoSelectValue(info: HisInformation, param: HisItemParameter): boolean {
    if (!param) { return false; }
    switch (param.informationKind) {
      case InformationKinds.Disease:
        return this.existHisInfoSelectValueFromTable(info.diseaseCollection);
      case InformationKinds.Hospitalization:
        return this.existHisInfoSelectValueFromTable(info.hospitalizationCollection);
      case InformationKinds.Operation:
        return this.existHisInfoSelectValueFromTable(info.operationCollection);
    }
    switch (param.screenKind) {
      case ScreenKinds.Department:
      case ScreenKinds.Doctor:
        return true;
      default:
        return false;
    }
  }

  private existHisInfoSelectValueFromTable(table: Table): boolean {
    return (table && table.rows && table.rows.length > 0);
  }

  public applyHisInformationToExtraSections(info: HisInformation, user: User, department: Department, orderUser: User, attendingUser: User) {
    const param = new HisInformationForceUpdateParameter();
    param.shouldUpdateExtraSectionOnly = true;
    param.shouldUpdatePatientInformation = true;
    param.shouldUpdateAllInformation = this._document.source.attributes.behavior.shouldUpdateAllHisItemsInExtraSectionWhenQuote;

    const action = new HisItemProgressAction(this._document, info, EditPhaseKinds.EditStartFirst, user, department, orderUser, attendingUser, true, this.hostName, param);
    this.applyByAction(action);
  }
}
